import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { enrollmentAPI } from '../services/api';

const ProgressContext = createContext(null);

export const useProgress = () => {
  const context = useContext(ProgressContext);
  if (!context) {
    throw new Error('useProgress must be used within a ProgressProvider');
  }
  return context;
};

export const ProgressProvider = ({ children }) => {
  const { user } = useAuth();
  const [progress, setProgress] = useState({
    userId: null,
    enrolledCourses: [],
    enrollmentDate: null,
    completedLessons: [],
    currentCourse: null,
    currentWeek: 1,
    badges: [],
    totalProgress: 0,
    unlockedRewards: [],
    unlocked_weeks: [1]
  });
  const [loading, setLoading] = useState(false);

  // Fetch progress from backend
  const fetchProgress = useCallback(async (courseId) => {
    if (!user || !courseId) return null;
    
    setLoading(true);
    try {
      const response = await enrollmentAPI.getProgress(courseId);
      const data = response.data;
      
      setProgress(prev => ({
        ...prev,
        userId: data.user_id,
        enrolledCourses: [courseId],
        enrollmentDate: data.enrollment_date || new Date().toISOString(),
        completedLessons: data.completed_lessons || [],
        currentCourse: courseId,
        currentWeek: data.current_week || 1,
        badges: data.badges || [],
        totalProgress: data.progress || 0,
        unlockedWeeks: data.unlocked_weeks || [1]
      }));
      
      return data;
    } catch (error) {
      // Not enrolled yet - that's okay
      if (error.response?.status === 404) {
        return null;
      }
      console.error('Error fetching progress:', error);
      return null;
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Enroll in a course
  const enrollInCourse = useCallback(async (courseId) => {
    if (!user) return null;
    
    try {
      const response = await enrollmentAPI.enroll(courseId);
      const data = response.data;
      
      setProgress(prev => ({
        ...prev,
        userId: data.user_id,
        enrolledCourses: [...prev.enrolledCourses, courseId],
        enrollmentDate: data.enrollment_date,
        completedLessons: data.completed_lessons || [],
        currentCourse: courseId,
        currentWeek: data.current_week || 1,
        badges: data.badges || [],
        totalProgress: data.progress || 0,
        unlocked_weeks: data.unlocked_weeks || [1]
      }));
      
      return data;
    } catch (error) {
      // Already enrolled
      if (error.response?.status === 400) {
        return fetchProgress(courseId);
      }
      console.error('Error enrolling:', error);
      throw error;
    }
  }, [user, fetchProgress]);

  // Complete a lesson
  const completeLesson = useCallback(async (lessonId, reward) => {
    if (!user || !progress.currentCourse) return null;
    
    try {
      const response = await enrollmentAPI.completeLesson(progress.currentCourse, lessonId);
      const data = response.data;
      
      if (!data.already_completed) {
        setProgress(prev => ({
          ...prev,
          completedLessons: data.completed_lessons || [...prev.completedLessons, lessonId],
          totalProgress: data.progress || prev.totalProgress,
          unlockedRewards: reward ? [...prev.unlockedRewards, reward] : prev.unlockedRewards
        }));
      }
      
      return reward;
    } catch (error) {
      console.error('Error completing lesson:', error);
      throw error;
    }
  }, [user, progress.currentCourse]);

  // Unlock week (local state update)
  const unlockWeek = useCallback((weekNumber) => {
    setProgress(prev => ({
      ...prev,
      currentWeek: weekNumber,
      unlocked_weeks: prev.unlocked_weeks.includes(weekNumber) 
        ? prev.unlocked_weeks 
        : [...prev.unlocked_weeks, weekNumber]
    }));
  }, []);

  // Add badge (local state update)
  const addBadge = useCallback((badge) => {
    setProgress(prev => {
      if (prev.badges.includes(badge)) return prev;
      return {
        ...prev,
        badges: [...prev.badges, badge]
      };
    });
  }, []);

  // Calculate progress percentage
  const calculateProgress = useCallback((totalLessons) => {
    return Math.round((progress.completedLessons.length / totalLessons) * 100);
  }, [progress.completedLessons]);

  // Check if enrolled in a course
  const isEnrolled = useCallback((courseId) => {
    return progress.enrolledCourses.includes(courseId);
  }, [progress.enrolledCourses]);

  const value = {
    progress,
    loading,
    fetchProgress,
    enrollInCourse,
    completeLesson,
    unlockWeek,
    addBadge,
    calculateProgress,
    isEnrolled
  };

  return <ProgressContext.Provider value={value}>{children}</ProgressContext.Provider>;
};
